/**
 * Тревоги (§2.5, дополнение): то же, что уходит в Telegram ops-чат
 * (`STAFF_ALERT_TG_CHAT_IDS` в `sharing`), но в приложении. Жалоба водителя:
 * «алерты, которые в Telegram уходят, чтобы и в приложении были».
 *
 * ─────────────────────────────────────────────────────────────────────────
 * РУЧКА ДЕЛАЕТСЯ ПАРАЛЛЕЛЬНО — 404/403 ЧИТАЮТСЯ КАК «СЕКЦИИ ПОКА НЕТ»
 * ─────────────────────────────────────────────────────────────────────────
 * `GET /v1/scout/alerts` — контракт, а не выкаченная ручка: пишем схему по
 * нему заранее (см. CLAUDE.md про C1/C2/C3), а до выката секция в «Заданиях»
 * просто не рисуется — как `fetchServerHubs` в `api/hubs.ts`. `retry:false`
 * по той же причине: незачем тратить три круга на маршрут, которого ещё нет.
 */
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, request } from '@/lib/http'
import { findByNumber } from '@/lib/bike-number'
import { displayNumber, type ScoutVehicle } from '@/api/vehicles'

export const scoutAlertSchema = z.object({
  id: z.string().default(''),
  // Свободная строка от бэкенда, не закрытый enum — см. alertKindLabel:
  // неизвестный kind обязан остаться видимой строкой, а не потеряться.
  kind: z.string().default(''),
  title: z.string().default(''),
  body: z.string().default(''),
  vehicleId: z.string().default(''),
  vehicleNumber: z.string().default(''),
  createdAt: z.string().default(''),
})
export type ScoutAlert = z.infer<typeof scoutAlertSchema>

const scoutAlertsReply = z.object({ alerts: z.array(scoutAlertSchema).default([]) })

/** `null` — ручка ещё не выкачена (404) или недоступна роли (403). */
async function fetchAlerts(limit: number): Promise<ScoutAlert[] | null> {
  try {
    const out = await request('/v1/scout/alerts', scoutAlertsReply, { query: { limit } })
    return out.alerts
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null
    throw e
  }
}

/** Обновление раз в 30 с, пока экран «Задания» открыт — та же частота, что фон карты (§2.4). */
const REFRESH_MS = 30_000

export function useScoutAlerts(limit = 50) {
  return useQuery({
    queryKey: ['scout.alerts', limit],
    queryFn: () => fetchAlerts(limit),
    refetchInterval: REFRESH_MS,
    retry: false,
  })
}

/**
 * Категория тревоги словами — четыре корзины из спецификации (кража/замок/
 * зона/прочее). `kind` со стороны бэкенда пока ничем не зафиксирован
 * (ручка в разработке параллельно), поэтому классификация — по подстроке, а
 * не точным сравнением: она обязана давать «Прочее» на любую будущую
 * строку, а не падать и не показывать пустой бейдж.
 */
export function alertKindLabel(kind: string): string {
  const k = kind.toLowerCase()
  if (k.includes('theft') || k.includes('alarm')) return 'Кража'
  if (k.includes('lock')) return 'Замок'
  if (k.includes('zone') || k.includes('geofence')) return 'Зона'
  return 'Прочее'
}

export type AlertTone = 'red' | 'orange' | 'purple' | 'grey'

/** Цвет бейджа — та же классификация, что и label, отдельная функция ради независимых тестов. */
export function alertKindTone(kind: string): AlertTone {
  const k = kind.toLowerCase()
  if (k.includes('theft') || k.includes('alarm')) return 'red'
  if (k.includes('lock')) return 'orange'
  if (k.includes('zone') || k.includes('geofence')) return 'purple'
  return 'grey'
}

/**
 * Байк по тревоге — сначала `vehicleId`, иначе резолв по номеру в уже
 * загруженном парке (`findByNumber`, как у `lib/scanner/resolve.ts`).
 * Синхронно и офлайн: тревога — уведомление, а не место для ещё одного
 * сетевого похода при каждом тапе. Неоднозначность (0 или >1 совпадений) —
 * `null`, строка остаётся без ссылки, а не ведёт наугад не туда.
 */
export function resolveAlertVehicleId(
  alert: Pick<ScoutAlert, 'vehicleId' | 'vehicleNumber'>,
  vehicles: readonly ScoutVehicle[],
): string | null {
  if (alert.vehicleId) return alert.vehicleId
  if (!alert.vehicleNumber) return null

  const numbered = vehicles.map((v) => ({ id: v.id, number: displayNumber(v) }))
  const matches = findByNumber(alert.vehicleNumber, numbered)
  return matches.length === 1 ? matches[0]!.id : null
}
